import { Injectable, signal } from '@angular/core';

// SCRUM-1489 — single source of truth for the user's IANA timezone in the
// browser. Hydrates synchronously from localStorage on cold boot (avoids
// waiting on /auth/profile before the first date renders), and is
// re-populated by:
//   1. AppComponent's boot-time profile fetch (authoritative from backend)
//   2. profile.component saves after the user picks a new zone
//
// TzDatePipe reads the current value via this.timezone() to format dates
// in the user's preferred zone.
@Injectable({ providedIn: 'root' })
export class TimezoneService {
  private static readonly STORAGE_KEY = 'hub_user_timezone';
  private static readonly FALLBACK = 'UTC';

  readonly timezone = signal<string>(this.loadInitial());

  setTimezone(tz: string | null | undefined): void {
    const clean = (tz ?? '').trim();
    if (!clean) return;
    if (clean === this.timezone()) return;
    try {
      localStorage.setItem(TimezoneService.STORAGE_KEY, clean);
    } catch { /* private mode / quota — ignore */ }
    this.timezone.set(clean);
  }

  private loadInitial(): string {
    try {
      const stored = localStorage.getItem(TimezoneService.STORAGE_KEY);
      if (stored) return stored;
    } catch { /* SSR / private mode */ }
    try {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detected) return detected;
    } catch { /* very old browser */ }
    return TimezoneService.FALLBACK;
  }
}
